export type QueueSortKey =
  | "access"
  | "brand"
  | "asin"
  | "rating"
  | "issue"
  | "review"
  | "lead";

export type QueueSortDirection = "asc" | "desc";

export type QueueSort = {
  key: QueueSortKey;
  direction: QueueSortDirection;
};

type SortableQueueReview = {
  id: string;
  asin: string;
  rating: number;
  title: string | null;
  body: string;
  productTitle: string | null;
  brand: { name: string } | null;
  classification: { violations: Array<{ type: string }> };
  auditRequest: { email: string; createdAt: string };
  customerStatus: "none" | "invite_sent" | "verified";
};

const ACCESS_ORDER = {
  verified: 0,
  invite_sent: 1,
  none: 2,
} as const;

const TEXT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const DEFAULT_QUEUE_SORT: QueueSort = {
  key: "access",
  direction: "asc",
};

function normalizeText(value: string | null | undefined) {
  return value?.replace(/\s+/g, " ").trim() ?? "";
}

function compareText(
  left: string | null | undefined,
  right: string | null | undefined,
  direction: QueueSortDirection
) {
  const normalizedLeft = normalizeText(left);
  const normalizedRight = normalizeText(right);

  if (!normalizedLeft && !normalizedRight) return 0;
  if (!normalizedLeft) return 1;
  if (!normalizedRight) return -1;

  const comparison = TEXT_COLLATOR.compare(normalizedLeft, normalizedRight);
  return direction === "asc" ? comparison : -comparison;
}

export function queueIssueText(review: SortableQueueReview) {
  return review.classification.violations
    .map((violation) => violation.type)
    .filter(Boolean)
    .join(", ");
}

function queueReviewText(review: SortableQueueReview) {
  return review.title || review.body;
}

function compareSelectedColumn(
  left: SortableQueueReview,
  right: SortableQueueReview,
  sort: QueueSort
) {
  const direction = sort.direction === "asc" ? 1 : -1;

  switch (sort.key) {
    case "access":
      return (
        (ACCESS_ORDER[left.customerStatus] - ACCESS_ORDER[right.customerStatus]) *
          direction ||
        compareText(left.brand?.name, right.brand?.name, "asc") ||
        compareText(left.asin, right.asin, "asc")
      );
    case "brand":
      return compareText(left.brand?.name, right.brand?.name, sort.direction);
    case "asin":
      return (
        compareText(left.asin, right.asin, sort.direction) ||
        compareText(left.productTitle, right.productTitle, sort.direction)
      );
    case "rating":
      return (left.rating - right.rating) * direction;
    case "issue":
      return compareText(
        queueIssueText(left),
        queueIssueText(right),
        sort.direction
      );
    case "review":
      return compareText(
        queueReviewText(left),
        queueReviewText(right),
        sort.direction
      );
    case "lead":
      return (
        compareText(
          left.auditRequest.email,
          right.auditRequest.email,
          sort.direction
        ) ||
        (new Date(left.auditRequest.createdAt).getTime() -
          new Date(right.auditRequest.createdAt).getTime()) *
          direction
      );
  }
}

export function sortQueueReviews<T extends SortableQueueReview>(
  reviews: readonly T[],
  sort: QueueSort
) {
  return [...reviews].sort((left, right) => {
    return (
      compareSelectedColumn(left, right, sort) ||
      new Date(left.auditRequest.createdAt).getTime() -
        new Date(right.auditRequest.createdAt).getTime() ||
      TEXT_COLLATOR.compare(left.id, right.id)
    );
  });
}

export function nextQueueSort(
  current: QueueSort,
  key: QueueSortKey
): QueueSort {
  if (current.key === key) {
    return {
      key,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }

  return {
    key,
    direction: key === "rating" ? "desc" : "asc",
  };
}
